import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { useAuth } from "./lib/auth";
import { Shell } from "./components/Shell";
import { RosterProvider } from "./lib/roster";
import { Login } from "./views/Login";
import { c } from "./lib/theme";

// Every page loads its code on first visit, so sign-in and the first page
// are fast; later pages arrive in a moment.
const Dashboard = lazy(() => import("./views/Dashboard").then((m) => ({ default: m.Dashboard })));
const ExecutiveView = lazy(() => import("./views/ExecutiveView").then((m) => ({ default: m.ExecutiveView })));
const ActionCenter = lazy(() => import("./views/ActionCenter").then((m) => ({ default: m.ActionCenter })));
const Metrics = lazy(() => import("./views/Metrics").then((m) => ({ default: m.Metrics })));
const Portfolio = lazy(() => import("./views/Portfolio").then((m) => ({ default: m.Portfolio })));
const Pipeline = lazy(() => import("./views/Pipeline").then((m) => ({ default: m.Pipeline })));
const ProcessModel = lazy(() => import("./views/ProcessModel").then((m) => ({ default: m.ProcessModel })));
const Connectors = lazy(() => import("./views/Connectors").then((m) => ({ default: m.Connectors })));
const Opportunities = lazy(() => import("./views/Opportunities").then((m) => ({ default: m.Opportunities })));
const OpportunityRecord = lazy(() => import("./views/OpportunityRecord").then((m) => ({ default: m.OpportunityRecord })));
const Companies = lazy(() => import("./views/Companies").then((m) => ({ default: m.Companies })));
const Reports = lazy(() => import("./views/Reports").then((m) => ({ default: m.Reports })));
const CompanyScoreboard = lazy(() => import("./views/CompanyScoreboard").then((m) => ({ default: m.CompanyScoreboard })));
const Match = lazy(() => import("./views/Match").then((m) => ({ default: m.Match })));
const PrimeReadiness = lazy(() => import("./views/PrimeReadiness").then((m) => ({ default: m.PrimeReadiness })));
const ReadinessBoard = lazy(() => import("./views/ReadinessBoard").then((m) => ({ default: m.ReadinessBoard })));
const FitProfile = lazy(() => import("./views/FitProfile").then((m) => ({ default: m.FitProfile })));
const ArclineReports = lazy(() => import("./views/ArclineReports").then((m) => ({ default: m.ArclineReports })));
const Glossary = lazy(() => import("./views/Glossary").then((m) => ({ default: m.Glossary })));
const ShipleyProcess = lazy(() => import("./views/ShipleyProcess").then((m) => ({ default: m.ShipleyProcess })));
const CompanyProposal = lazy(() => import("./views/CompanyProposal").then((m) => ({ default: m.CompanyProposal })));
const Admin = lazy(() => import("./views/Admin").then((m) => ({ default: m.Admin })));
const Documents = lazy(() => import("./views/Documents").then((m) => ({ default: m.Documents })));
const Vault = lazy(() => import("./views/Vault").then((m) => ({ default: m.Vault })));
const Notifications = lazy(() => import("./views/Notifications").then((m) => ({ default: m.Notifications })));
const Feedback = lazy(() => import("./views/Feedback").then((m) => ({ default: m.Feedback })));
const Onboarding = lazy(() => import("./views/Onboarding").then((m) => ({ default: m.Onboarding })));
const Proposals = lazy(() => import("./views/Proposals").then((m) => ({ default: m.Proposals })));
const Establishment = lazy(() => import("./views/Establishment").then((m) => ({ default: m.Establishment })));
const UsgTransitionChecklist = lazy(() => import("./views/UsgTransitionChecklist").then((m) => ({ default: m.UsgTransitionChecklist })));
const Capital = lazy(() => import("./views/Capital").then((m) => ({ default: m.Capital })));
const IpoDashboard = lazy(() => import("./views/IpoDashboard").then((m) => ({ default: m.IpoDashboard })));
const ProcessExplorer = lazy(() => import("./views/ProcessExplorer").then((m) => ({ default: m.ProcessExplorer })));
const ProcessMap = lazy(() => import("./views/ProcessMap").then((m) => ({ default: m.ProcessMap })));
const DocumentRegister = lazy(() => import("./views/DocumentRegister").then((m) => ({ default: m.DocumentRegister })));
const Lineage = lazy(() => import("./views/Lineage").then((m) => ({ default: m.Lineage })));
const ListingReadiness = lazy(() => import("./views/ListingReadiness").then((m) => ({ default: m.ListingReadiness })));
const LegalChecklist = lazy(() => import("./views/LegalChecklist").then((m) => ({ default: m.LegalChecklist })));

// "/" always means "your home" — which board that resolves to depends on
// role. Admins/partners land on the Executive Summary; everyone else lands
// on the operational Command Dashboard. Both are always directly reachable
// at their own stable route (/executive, /dashboard) regardless of role.
function HomeRoute() {
  const { me } = useAuth();
  // A company user lands on their own company.
  if (me?.organization === "company") return <Navigate to={me.companyIds?.length ? `/companies?company=${me.companyIds[0]}` : "/glossary"} replace />;
  const isLeadership = me?.role === "admin" || me?.role === "partner";
  return <Navigate to={isLeadership ? "/executive" : "/dashboard"} replace />;
}

// /companies/42 (used by pipeline cards, dashboard widgets and older links)
// opens the company list with that company selected.
function CompanyLink() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/companies?company=${Number(id) || ""}`} replace />;
}

export function App() {
  const { me, loading, signedInNoAccess, logout } = useAuth();

  if (loading) {
    return <div style={{ display: "grid", placeItems: "center", height: "100%", color: c.muted }}>Loading…</div>;
  }
  // Clerk mode: authenticated with the provider, but no ArcLine account is
  // linked to that identity (admin-provisioned tool — no self-serve access).
  if (signedInNoAccess) {
    return (
      <div style={{ display: "grid", placeItems: "center", height: "100%", color: c.muted, textAlign: "center", gap: 14, padding: 24 }}>
        <div>
          <div style={{ color: "#fff", fontSize: 18, fontWeight: 700, marginBottom: 8 }}>No ArcLine access</div>
          <div style={{ fontSize: 15, maxWidth: 360, lineHeight: 1.6 }}>
            You're signed in, but this account isn't provisioned in ArcLine. Ask an
            administrator to add your email, then sign in again.
          </div>
          <button onClick={logout} style={{ marginTop: 16, background: "transparent", color: c.muted, border: `1px solid ${c.muted}`, padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontSize: 14 }}>Sign out</button>
        </div>
      </div>
    );
  }
  if (!me) return <Login />;

  return (
    <RosterProvider>
    <Shell>
      <Suspense fallback={<div style={{ padding: 24, color: c.muted }}>Loading…</div>}>
      <Routes>
        <Route path="/" element={<HomeRoute />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/executive" element={<ExecutiveView />} />
        <Route path="/actions" element={<ActionCenter />} />
        <Route path="/metrics" element={<Metrics />} />
        <Route path="/portfolio" element={<Portfolio />} />
        <Route path="/pipeline" element={<Pipeline />} />
        <Route path="/process-model" element={<ProcessModel />} />
        <Route path="/connectors" element={<Connectors />} />
        <Route path="/opportunities" element={<Opportunities />} />
        <Route path="/opportunities/:id" element={<OpportunityRecord />} />
        <Route path="/companies" element={<Companies />} />
        <Route path="/companies/potential" element={<Companies key="potential" segment="potential" />} />
        <Route path="/companies/onboarded" element={<Companies key="onboarded" segment="onboarded" />} />
        <Route path="/companies/:id" element={<CompanyLink />} />
        <Route path="/companies/:id/fit" element={<FitProfile />} />
        <Route path="/companies/:id/readiness" element={<PrimeReadiness />} />
        <Route path="/readiness" element={<ReadinessBoard />} />
        <Route path="/companies/:id/proposals/:pid" element={<CompanyProposal />} />
        <Route path="/fit/prime" element={<FitProfile key="prime" prime />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/scoring" element={<CompanyScoreboard />} />
        <Route path="/match" element={<Match />} />
        <Route path="/arcline-reports" element={<ArclineReports />} />
        <Route path="/opportunity-analysis" element={<Navigate to="/arcline-reports" replace />} />
        <Route path="/listing" element={<IpoDashboard />} />
        <Route path="/listing/map" element={<ProcessMap />} />
        <Route path="/listing/roadmap" element={<ProcessExplorer />} />
        <Route path="/listing/register" element={<DocumentRegister />} />
        <Route path="/listing/lineage" element={<Lineage />} />
        <Route path="/listing/readiness" element={<ListingReadiness />} />
        <Route path="/listing/legal" element={<LegalChecklist />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/establishment" element={<Establishment />} />
        <Route path="/usg-transition" element={<UsgTransitionChecklist />} />
        <Route path="/proposals" element={<Navigate to="/proposals/process" replace />} />
        <Route path="/proposals/process" element={<ShipleyProcess />} />
        <Route path="/proposals/develop" element={<Proposals key="develop" tab="develop" />} />
        <Route path="/proposals/submit" element={<Proposals key="submit" tab="submit" />} />
        <Route path="/proposals/track" element={<Proposals key="track" tab="track" />} />
        <Route path="/capital" element={<Capital />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/vault" element={<Vault />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/feedback" element={<Feedback />} />
        <Route path="/glossary" element={<Glossary />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </Shell>
    </RosterProvider>
  );
}
